/**
 * Validation of catalog items (admin panel, server catalog store, tests) and sanitising of loadouts
 * received from the network. Every check is strict: unknown fields are dropped, wrong values make
 * an item invalid, and a loadout keeps only owned, known and correctly typed items.
 */
import { checkColorSet, checkNameColor, isHex } from "./contrast";
import { validFlagPixels } from "./flagArt";
import {
  BORDER_PATTERNS,
  CAPITAL_STYLES,
  CARD_BACKGROUNDS,
  CARD_FRAMES,
  COSMETIC_SOURCES,
  COSMETIC_TYPES,
  CUSTOM_CREATION_TYPES,
  CUSTOM_ITEM_IDS,
  EMOTE_GLYPHS,
  FILL_TEXTURES,
  FLAG_MAX_COLORS,
  MAX_EMOTE_CODE,
  MAX_EMOTES,
  MAX_LOADOUT_JSON,
  MUSIC_THEMES,
  NUKE_STYLES,
  PARTICLE_SHAPES,
  RARITIES,
  SINGLE_SLOTS,
  SOUND_VOICES,
  TRAIL_STYLES,
  VICTORY_ANIMATIONS,
  VICTORY_SOUNDS,
  emptyLoadout,
  type CardParams,
  type ColorSetParams,
  type CosmeticItem,
  type CosmeticParamsByType,
  type CosmeticType,
  type CustomCreations,
  type FlagParams,
  type Loadout,
  type LocalText,
  type PriceHint,
  type SingleSlot,
  type VictoryParams,
} from "./schema";
import type { CatalogIndex } from "./catalog";
import { CARD_TEXT_MAX, VICTORY_TEXT_MAX, checkCustomText } from "./wordFilter";

export const ITEM_ID_RE = /^[a-z0-9][a-z0-9.-]{2,47}$/;
export const MAX_NAME_CHARS = 32;
export const MAX_DESC_CHARS = 90;
export const MAX_PRICE = 100000;

type Obj = Record<string, unknown>;
const isObj = (v: unknown): v is Obj => typeof v === "object" && v !== null && !Array.isArray(v);
const oneOf = <T extends string>(list: readonly T[], v: unknown): v is T => typeof v === "string" && (list as readonly string[]).includes(v);
const intIn = (v: unknown, lo: number, hi: number): v is number => typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi;

function hexList(v: unknown, min: number, max: number): string[] | null {
  if (!Array.isArray(v) || v.length < min || v.length > max) return null;
  return v.every(isHex) ? (v as string[]).slice() : null;
}

function localText(v: unknown, max: number): LocalText | null {
  if (!isObj(v)) return null;
  const en = typeof v.en === "string" ? v.en.trim() : "";
  const nl = typeof v.nl === "string" ? v.nl.trim() : "";
  if (!en || !nl || en.length > max || nl.length > max) return null;
  return { en, nl };
}

function price(v: unknown): PriceHint | null | undefined {
  if (v === null) return null;
  if (!isObj(v)) return undefined;
  const out: PriceHint = {};
  if (v.coins !== undefined) {
    if (!intIn(v.coins, 0, MAX_PRICE)) return undefined;
    out.coins = v.coins;
  }
  if (v.crystals !== undefined) {
    if (!intIn(v.crystals, 0, MAX_PRICE)) return undefined;
    out.crystals = v.crystals;
  }
  return out.coins === undefined && out.crystals === undefined ? undefined : out;
}

function cardParams(v: Obj): CardParams | null {
  const colors = hexList(v.colors, 2, 2);
  if (!oneOf(CARD_BACKGROUNDS, v.background) || !colors || !oneOf(CARD_FRAMES, v.frame) || !isHex(v.textColor)) return null;
  if (!Array.isArray(v.badges) || v.badges.length > 3 || !v.badges.every((b) => oneOf(EMOTE_GLYPHS, b))) return null;
  const out: CardParams = { background: v.background, colors: [colors[0], colors[1]], frame: v.frame, badges: v.badges.slice(), textColor: v.textColor };
  if (v.text !== undefined) {
    const c = checkCustomText(v.text, CARD_TEXT_MAX);
    if (!c.ok) return null;
    out.text = c.text;
  }
  return out;
}

function flagParams(v: Obj): FlagParams | null {
  const palette = hexList(v.palette, 2, FLAG_MAX_COLORS);
  if (!palette || !validFlagPixels(v.pixels, palette.length)) return null;
  return { pixels: v.pixels, palette };
}

function victoryParams(v: Obj): VictoryParams | null {
  const colors = hexList(v.colors, 1, 5);
  if (!oneOf(VICTORY_ANIMATIONS, v.animation) || !oneOf(VICTORY_SOUNDS, v.sound) || !colors) return null;
  const out: VictoryParams = { animation: v.animation, sound: v.sound, colors };
  if (v.text !== undefined) {
    const c = checkCustomText(v.text, VICTORY_TEXT_MAX);
    if (!c.ok) return null;
    out.text = c.text;
  }
  return out;
}

function colorSetParams(v: Obj): ColorSetParams | null {
  if (!isHex(v.territory) || !isHex(v.border)) return null;
  if (!checkColorSet({ territory: v.territory, border: v.border }).ok) return null;
  const out: ColorSetParams = { territory: v.territory, border: v.border };
  if (v.pattern !== undefined) {
    if (typeof v.pattern !== "string" || (v.pattern !== "" && !ITEM_ID_RE.test(v.pattern))) return null;
    out.pattern = v.pattern;
  }
  return out;
}

/** Validated params of one type (unknown fields dropped), or null. */
export function validateParams<T extends CosmeticType>(type: T, raw: unknown): CosmeticParamsByType[T] | null {
  if (!isObj(raw)) return null;
  const v = raw;
  let out: unknown = null;
  switch (type as CosmeticType) {
    case "border":
      out = oneOf(BORDER_PATTERNS, v.pattern) && isHex(v.accent) ? { pattern: v.pattern, accent: v.accent } : null;
      break;
    case "fill":
      out = oneOf(FILL_TEXTURES, v.texture) && intIn(v.strength, 1, 3) ? { texture: v.texture, strength: v.strength } : null;
      break;
    case "colorSet":
      out = colorSetParams(v);
      break;
    case "expansion":
    case "conquest": {
      const colors = hexList(v.colors, 1, 3);
      out = oneOf(PARTICLE_SHAPES, v.shape) && colors ? { shape: v.shape, colors } : null;
      break;
    }
    case "capital":
      out = oneOf(CAPITAL_STYLES, v.style) && isHex(v.banner) && isHex(v.roof) ? { style: v.style, banner: v.banner, roof: v.roof } : null;
      break;
    case "nuke": {
      const colors = hexList(v.colors, 3, 3);
      out = oneOf(NUKE_STYLES, v.style) && colors ? { style: v.style, colors: [colors[0], colors[1], colors[2]] } : null;
      break;
    }
    case "trail": {
      const colors = hexList(v.colors, 1, 5);
      out = oneOf(TRAIL_STYLES, v.style) && colors && intIn(v.length, 3, 8) ? { style: v.style, colors, length: v.length } : null;
      break;
    }
    case "emote": {
      const colors = hexList(v.colors, 2, 2);
      out = oneOf(EMOTE_GLYPHS, v.glyph) && colors && intIn(v.code, 0, MAX_EMOTE_CODE) ? { glyph: v.glyph, colors: [colors[0], colors[1]], code: v.code } : null;
      break;
    }
    case "soundPack":
      out = oneOf(SOUND_VOICES, v.voice) && intIn(v.pitch, -6, 6) ? { voice: v.voice, pitch: v.pitch } : null;
      break;
    case "music":
      out = oneOf(MUSIC_THEMES, v.theme) ? { theme: v.theme } : null;
      break;
    case "victory":
      out = victoryParams(v);
      break;
    case "nameColor":
      out = isHex(v.color) && isHex(v.outline) && checkNameColor({ color: v.color, outline: v.outline }).ok ? { color: v.color, outline: v.outline } : null;
      break;
    case "title": {
      const text = localText(v.text, 24);
      out = text ? { text } : null;
      break;
    }
    case "card":
      out = cardParams(v);
      break;
    case "flag":
      out = flagParams(v);
      break;
  }
  return out as CosmeticParamsByType[T] | null;
}

export type ItemCheck = { ok: true; item: CosmeticItem } | { ok: false; errors: string[] };

/** Validate one catalog item (admin input, stored overrides). Returns a clean copy. */
export function validateItem(raw: unknown): ItemCheck {
  const errors: string[] = [];
  if (!isObj(raw)) return { ok: false, errors: ["notAnObject"] };
  const id = raw.id;
  if (typeof id !== "string" || !ITEM_ID_RE.test(id)) errors.push("id");
  if (!oneOf(COSMETIC_TYPES, raw.type)) errors.push("type");
  if (!oneOf(RARITIES, raw.rarity)) errors.push("rarity");
  if (!oneOf(COSMETIC_SOURCES, raw.source)) errors.push("source");
  const name = localText(raw.name, MAX_NAME_CHARS);
  if (!name) errors.push("name");
  const desc = localText(raw.desc, MAX_DESC_CHARS);
  if (!desc) errors.push("desc");
  const p = price(raw.price);
  if (p === undefined) errors.push("price");
  if (raw.set !== undefined && (typeof raw.set !== "string" || !ITEM_ID_RE.test(raw.set))) errors.push("set");
  const params = oneOf(COSMETIC_TYPES, raw.type) ? validateParams(raw.type, raw.params) : null;
  if (!params) errors.push("params");
  if (errors.length > 0) return { ok: false, errors };
  const item = {
    id: id as string,
    type: raw.type as CosmeticType,
    rarity: raw.rarity,
    name: name!,
    desc: desc!,
    price: p!,
    source: raw.source,
    params,
  } as CosmeticItem;
  if (typeof raw.set === "string") item.set = raw.set;
  if (raw.editable === true) item.editable = true;
  if (raw.hidden === true) item.hidden = true;
  return { ok: true, item };
}

export interface CatalogCheck {
  ok: boolean;
  errors: { id: string; errors: string[] }[];
}

/** Validate a whole catalog: every item valid, unique ids, unique emote codes. */
export function validateCatalog(items: readonly unknown[]): CatalogCheck {
  const errors: { id: string; errors: string[] }[] = [];
  const ids = new Set<string>();
  const codes = new Set<number>();
  for (const raw of items) {
    const r = validateItem(raw);
    const id = isObj(raw) && typeof raw.id === "string" ? raw.id : "?";
    if (!r.ok) {
      errors.push({ id, errors: r.errors });
      continue;
    }
    if (ids.has(r.item.id)) errors.push({ id, errors: ["duplicateId"] });
    ids.add(r.item.id);
    if (r.item.type === "emote") {
      if (codes.has(r.item.params.code)) errors.push({ id, errors: ["duplicateEmoteCode"] });
      codes.add(r.item.params.code);
    }
  }
  return { ok: errors.length === 0, errors };
}

/** Ownership test: true when the player may wear the item (defaults are always allowed). */
export type OwnsItem = (itemId: string) => boolean;

const CUSTOM_VALIDATORS: { [K in keyof CustomCreations]-?: (v: Obj) => CustomCreations[K] | null } = {
  card: cardParams,
  flag: flagParams,
  victory: victoryParams,
  colorSet: colorSetParams,
};

/**
 * Clean a loadout from the network or storage: only known items in the slot of their own type,
 * only owned items (when `owns` is given), at most MAX_EMOTES distinct emotes, and custom creations
 * only when their editable item is equipped and they pass the checks (word filter, contrast, size).
 * Never throws; garbage gives an empty loadout.
 */
export function sanitizeLoadout(raw: unknown, index: CatalogIndex, owns?: OwnsItem): Loadout {
  const out = emptyLoadout();
  if (!isObj(raw)) return out;
  try {
    if (JSON.stringify(raw).length > MAX_LOADOUT_JSON) return out;
  } catch {
    return out;
  }
  const allowed = (id: unknown): id is string => typeof id === "string" && index.has(id) && (!owns || owns(id));
  if (isObj(raw.slots)) {
    for (const slot of SINGLE_SLOTS) {
      const id = raw.slots[slot];
      if (!allowed(id)) continue;
      const item = index.get(id)!;
      if (item.type === slot) out.slots[slot as SingleSlot] = id;
    }
  }
  if (Array.isArray(raw.emotes)) {
    for (const id of raw.emotes) {
      if (out.emotes.length >= MAX_EMOTES) break;
      if (!allowed(id) || out.emotes.includes(id) || index.get(id)!.type !== "emote") continue;
      out.emotes.push(id);
    }
  }
  if (isObj(raw.custom)) {
    for (const type of CUSTOM_CREATION_TYPES) {
      const v = raw.custom[type];
      if (!isObj(v)) continue;
      // Only kept while the editable item is equipped in that slot.
      if (out.slots[type] !== CUSTOM_ITEM_IDS[type]) continue;
      const clean = CUSTOM_VALIDATORS[type](v);
      if (clean) (out.custom as Record<string, unknown>)[type] = clean;
    }
  }
  return out;
}

/** The params a player wears per slot (custom creations applied). Render code uses this. */
export interface ResolvedLoadout {
  border?: CosmeticParamsByType["border"];
  fill?: CosmeticParamsByType["fill"];
  colorSet?: CosmeticParamsByType["colorSet"];
  expansion?: CosmeticParamsByType["expansion"];
  conquest?: CosmeticParamsByType["conquest"];
  capital?: CosmeticParamsByType["capital"];
  nuke?: CosmeticParamsByType["nuke"];
  trail?: CosmeticParamsByType["trail"];
  soundPack?: CosmeticParamsByType["soundPack"];
  music?: CosmeticParamsByType["music"];
  victory?: CosmeticParamsByType["victory"];
  nameColor?: CosmeticParamsByType["nameColor"];
  title?: CosmeticParamsByType["title"];
  card?: CosmeticParamsByType["card"];
  flag?: CosmeticParamsByType["flag"];
  emotes: CosmeticParamsByType["emote"][];
}

/**
 * The part of a loadout shown in the lobby list (title, calling card, flag and their custom
 * creations), so lobby broadcasts stay small; undefined when none of it is worn.
 */
export function lobbyLoadout(l: Loadout | undefined): Loadout | undefined {
  if (!l) return undefined;
  const out: Loadout = { slots: {}, emotes: [], custom: {} };
  for (const slot of ["title", "card", "flag"] as const) if (l.slots[slot]) out.slots[slot] = l.slots[slot];
  if (out.slots.card && l.custom.card) out.custom.card = l.custom.card;
  if (out.slots.flag && l.custom.flag) out.custom.flag = l.custom.flag;
  return Object.keys(out.slots).length > 0 ? out : undefined;
}

/** Resolve a (sanitised) loadout into render parameters. */
export function resolveLoadout(loadout: Loadout, index: CatalogIndex): ResolvedLoadout {
  const out: ResolvedLoadout = { emotes: [] };
  const o = out as unknown as Record<string, unknown>;
  for (const slot of SINGLE_SLOTS) {
    const id = loadout.slots[slot];
    if (!id) continue;
    const item = index.get(id);
    if (!item || item.type !== slot) continue;
    const custom = (loadout.custom as Record<string, unknown>)[slot];
    o[slot] = item.editable && custom ? custom : item.params;
  }
  // A colour set may carry a border pattern.
  const pat = out.colorSet?.pattern;
  if (pat && !out.border) {
    const item = index.get(pat);
    if (item && item.type === "border") out.border = item.params;
  }
  for (const id of loadout.emotes) {
    const item = index.get(id);
    if (item && item.type === "emote") out.emotes.push(item.params);
  }
  return out;
}

/** True when the resolved loadout wears nothing (fast path for renderers). */
export function isEmptyResolved(r: ResolvedLoadout): boolean {
  for (const k in r) if (k !== "emotes" && (r as unknown as Record<string, unknown>)[k] !== undefined) return false;
  return r.emotes.length === 0;
}

/**
 * Structural parse of a loadout from the network WITHOUT the catalog (src/shared/validate.ts uses
 * it for `hello.cosmetics` / `setCosmetics`): well-formed ids per known slot, at most MAX_EMOTES
 * emotes, custom creations that pass their checks, JSON <= MAX_LOADOUT_JSON. The server then runs
 * `sanitizeLoadout` with the live catalog and the player's inventory. Returns null for garbage.
 */
export function parseLoadoutShape(raw: unknown): Loadout | null {
  if (!isObj(raw)) return null;
  try {
    if (JSON.stringify(raw).length > MAX_LOADOUT_JSON) return null;
  } catch {
    return null;
  }
  const out = emptyLoadout();
  if (raw.slots !== undefined) {
    if (!isObj(raw.slots)) return null;
    for (const slot of SINGLE_SLOTS) {
      const id = raw.slots[slot];
      if (typeof id === "string" && ITEM_ID_RE.test(id)) out.slots[slot] = id;
    }
  }
  if (Array.isArray(raw.emotes)) {
    for (const id of raw.emotes) {
      if (out.emotes.length >= MAX_EMOTES) break;
      if (typeof id === "string" && ITEM_ID_RE.test(id) && !out.emotes.includes(id)) out.emotes.push(id);
    }
  }
  if (isObj(raw.custom)) {
    for (const type of CUSTOM_CREATION_TYPES) {
      const v = raw.custom[type];
      if (!isObj(v)) continue;
      const clean = CUSTOM_VALIDATORS[type](v);
      if (clean) (out.custom as Record<string, unknown>)[type] = clean;
    }
  }
  return out;
}
